import { useState } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from "@floating-ui/react";
import { css, SerializedStyles } from "@emotion/react";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { FONTS, fontSize } from "../../../styles/fonts";
import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { ReactComponent as InfoIcon } from "../../../assets/icons/info.svg";

export const SwarovskiTooltip: ReactTFC<{
  children: React.ReactNode;
  content: React.ReactNode;
  styles?: Array<SerializedStyles>;
}> = props => {
  const { children, content } = props;
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "top",
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(15),
      flip({
        fallbackAxisSideDirection: "start",
      }),
      shift(),
    ],
  });

  // Event listeners to change the open state
  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  // Role props for screen readers
  const role = useRole(context, { role: "tooltip" });

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  return (
    <>
      <div
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {children}
      </div>
      <FloatingPortal>
        {isOpen && (
          <div
            css={[fontSize.x2s, FONTS.SwarovskiFutura300, TOOLTIP_STYLES, ...(props.styles || [])]}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {content}
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

export const SwarovskiInfoTooltip: ReactTFC<{
  content: React.ReactNode;
  styles?: Array<SerializedStyles>;
}> = props => {
  return (
    <SwarovskiTooltip {...props}>
      <InfoIcon
        css={css`
          display: flex;
          width: 16px;
          height: 16px;
        `}
      />
    </SwarovskiTooltip>
  );
};

const TOOLTIP_STYLES = css`
  padding: 8px;
  border-radius: 6px;
  background-color: ${CSS_COLORS.GreenWhite};
  box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.2);
  white-space: pre-line;
  z-index: 10000000000;
`;
